import { useCallback, useState } from 'react'
import BackofficePanel, { PanelError, Row } from '../BackofficePanel'
import { useAdminPoll } from '../../../app/backoffice/useAdminPoll'
import {
  clearBackoff,
  forceSchedulerTick,
  jumpAdminClock,
  pauseScheduler,
  rescoreGameweek,
  resetAdminClock,
  resumeScheduler,
  runGameweekAction,
  setAdminClock,
  simulateSeason,
  getAdminControl,
} from '../../../api/admin'
import type { AdminControlState } from '../../../adminApi'
import type { DevTool } from '../../../devToolCapabilities'

/**
 * The season & gameweek control panel.
 *
 * **The grouping is the safety feature.** Controls that *move the pipeline's input* — the replica
 * clock, a forced sync, pause/resume — are rendered under one heading; controls that *write the
 * pipeline's output* — simulate deadline, finalize, simulate season — under another, with a
 * plain-language warning that they write fabricated state. They are never interleaved for
 * layout's sake.
 *
 * Every refused control explains itself in one line rather than greying out silently: the server
 * sends the reason with the capability, so the panel never has to guess it.
 */

const CONTROL_POLL_MS = 15_000

export default function SeasonControlPanel({
  enabled,
  onToast,
}: {
  enabled: boolean
  onToast: (message: string) => void
}) {
  const load = useCallback(() => getAdminControl(), [])
  const poll = useAdminPoll(load, { enabled, intervalMs: CONTROL_POLL_MS })
  const [busy, setBusy] = useState(false)
  const [gameweek, setGameweek] = useState<number | null>(null)
  const state = poll.data

  const run = async (label: string, action: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await action()
      poll.refresh()
      onToast(`${label} — done.`)
    } catch (failure) {
      onToast(`${label} failed: ${failure instanceof Error ? failure.message : String(failure)}`)
    } finally {
      setBusy(false)
    }
  }

  const target = gameweek ?? state?.currentGameweek ?? 1
  const can = (tool: DevTool) => state?.capabilities[tool]?.available ?? false
  const why = (tool: DevTool) => state?.capabilities[tool]?.reason ?? ''

  return (
    <BackofficePanel
      title="Season & gameweek"
      badge={state ? `GW${state.currentGameweek ?? '?'}` : '…'}
      defaultOpen={false}
    >
      {poll.error !== null && <PanelError error={poll.error} />}
      {state && (
        <>
          <div className="bo-field">
            <label className="bo-field__label" htmlFor="bo-gameweek">
              Gameweek
            </label>
            <input
              id="bo-gameweek"
              className="bo-input"
              type="number"
              min={1}
              value={target}
              onChange={(e) => setGameweek(Number(e.target.value))}
            />
          </div>
          <GameweekState state={state} gameweek={target} />

          {/* ── Group 1: move the pipeline's input ──────────────────────────────────────── */}
          <div className="bo-group">
            <p className="bo-group__title">Move the pipeline's input</p>

            <Gated tool="replicaClock" can={can} why={why}>
              {/* The jump buttons are the point: "advance the replica clock and observe", in one
                  click. */}
              <div className="bo-btn-row">
                {(['next-deadline', 'next-kickoff', 'next-fulltime'] as const).map((to) => (
                  <button
                    key={to}
                    type="button"
                    className="bo-btn"
                    disabled={busy}
                    onClick={() =>
                      void run(`Jump to ${to}`, async () => {
                        const result = await jumpAdminClock(to)
                        onToast(result.jump.label)
                      })
                    }
                  >
                    Jump to {to.replace('next-', 'next ')}
                  </button>
                ))}
              </div>
              <div className="bo-btn-row">
                {[
                  ['−1h', -60],
                  ['−15m', -15],
                  ['+15m', 15],
                  ['+1h', 60],
                  ['+1d', 60 * 24],
                ].map(([label, minutes]) => (
                  <button
                    key={String(label)}
                    type="button"
                    className="bo-btn"
                    disabled={busy}
                    onClick={() =>
                      void run(`Clock ${label}`, () =>
                        setAdminClock({ advanceMinutes: minutes as number })
                      )
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="bo-btn-row">
                {/* Frozen is the default and the honest one — `clock.ts` defaults to it for
                    exactly this reason. */}
                {[0, 1, 60, 600].map((speed) => (
                  <button
                    key={speed}
                    type="button"
                    className="bo-btn"
                    disabled={busy}
                    onClick={() => void run(`Speed ×${speed}`, () => setAdminClock({ speed }))}
                  >
                    ×{speed}
                  </button>
                ))}
                <button
                  type="button"
                  className="bo-btn"
                  disabled={busy}
                  onClick={() => void run('Reset clock', () => resetAdminClock())}
                >
                  Reset clock
                </button>
              </div>
            </Gated>

            <Gated tool="schedulerControl" can={can} why={why}>
              <div className="bo-btn-row">
                <button
                  type="button"
                  className="bo-btn"
                  disabled={busy}
                  onClick={() => void run('Force a sync pass', () => forceSchedulerTick())}
                >
                  Force sync now
                </button>
                {state.scheduler.paused ? (
                  <button
                    type="button"
                    className="bo-btn"
                    disabled={busy}
                    onClick={() => void run('Resume scheduler', () => resumeScheduler())}
                  >
                    Resume scheduler
                  </button>
                ) : (
                  <button
                    type="button"
                    className="bo-btn"
                    disabled={busy}
                    onClick={() => {
                      const reason = window.prompt('Why are you pausing syncing?')
                      if (reason) void run('Pause scheduler', () => pauseScheduler(reason))
                    }}
                  >
                    Pause scheduler
                  </button>
                )}
              </div>
              {state.scheduler.paused && (
                <p className="bo-error">
                  paused: {state.scheduler.pausedReason ?? 'no reason recorded'}
                </p>
              )}
              {/* After fixing a bad key you want the next tick, not the one in 55 minutes. */}
              {Object.entries(state.scheduler.backoff).map(([resource, backoff]) => (
                <div key={resource} className="bo-btn-row">
                  <span className="bo-mono">
                    {resource} in backoff until {backoff.until} (×{backoff.consecutiveFailures})
                  </span>
                  <button
                    type="button"
                    className="bo-btn"
                    disabled={busy}
                    onClick={() => void run(`Clear ${resource} backoff`, () => clearBackoff(resource))}
                  >
                    Clear backoff
                  </button>
                </div>
              ))}
            </Gated>
          </div>

          {/* ── Group 2: write the pipeline's output ────────────────────────────────────── */}
          <div className="bo-group">
            <p className="bo-group__title">Write the pipeline's output</p>
            <p className="bo-group__warning">
              These write fabricated state directly. In a mode where the scheduler derives the same
              flags, it will overwrite them on the next tick — which is why most are refused there.
            </p>

            <Gated tool="simulateDeadline" can={can} why={why}>
              <button
                type="button"
                className="bo-btn bo-btn--danger"
                disabled={busy}
                onClick={() => void run(`Lock GW${target}`, () => runGameweekAction('deadline', target))}
              >
                Lock deadline (GW{target})
              </button>
            </Gated>
            <Gated tool="revealResults" can={can} why={why}>
              <button
                type="button"
                className="bo-btn bo-btn--danger"
                disabled={busy}
                onClick={() => void run(`Reveal GW${target}`, () => runGameweekAction('reveal', target))}
              >
                Reveal results (GW{target})
              </button>
            </Gated>
            <Gated tool="finalizeGameweek" can={can} why={why}>
              <button
                type="button"
                className="bo-btn bo-btn--danger"
                disabled={busy}
                onClick={() =>
                  void run(`Finalize GW${target}`, () => runGameweekAction('finalize', target))
                }
              >
                Finalize &amp; advance (GW{target})
              </button>
            </Gated>
            <Gated tool="simulateSeason" can={can} why={why}>
              <button
                type="button"
                className="bo-btn bo-btn--danger"
                disabled={busy}
                onClick={() => {
                  // A typed confirmation: it writes 26+ gameweeks for every user, with no undo.
                  const confirmed = window.prompt('Type SIMULATE to write the rest of the season')
                  if (confirmed) void run('Simulate season', () => simulateSeason(undefined, confirmed))
                }}
              >
                Simulate rest of season
              </button>
            </Gated>

            {/* Re-score is in this group because it rewrites scored rows — but unlike its
                neighbours it fabricates nothing, which is why it survives in live. */}
            <Gated tool="rescoreGameweek" can={can} why={why}>
              <button
                type="button"
                className="bo-btn"
                disabled={busy}
                onClick={() =>
                  void run(`Re-score GW${target}`, async () => {
                    const outcome = await rescoreGameweek(target)
                    onToast(`Re-scored GW${target}: ${outcome.changed.length} user(s) moved.`)
                  })
                }
              >
                Re-score GW{target}
              </button>
            </Gated>
          </div>
        </>
      )}
    </BackofficePanel>
  )
}

/**
 * Renders a control, or the one-line reason it is refused.
 *
 * Hiding a control is tempting: *"a greyed-out 'simulate rest of season' is an invitation to find
 * out why it's greyed out"* — true of a button with no explanation. A control that says why is
 * information, not an invitation.
 */
function Gated({
  tool,
  can,
  why,
  children,
}: {
  tool: DevTool
  can: (tool: DevTool) => boolean
  why: (tool: DevTool) => string
  children: React.ReactNode
}) {
  if (can(tool)) return <>{children}</>
  return (
    <p className="bo-reason">
      <strong>{tool}</strong> — unavailable in this mode. {why(tool)}
    </p>
  )
}

function GameweekState({ state, gameweek }: { state: AdminControlState; gameweek: number }) {
  const row = state.gameweeks.find((entry) => entry.gameweek === gameweek)
  if (!row) return <p className="bo-empty">GW{gameweek} has no fixtures.</p>
  return (
    <>
      <Row
        label={`GW${row.gameweek} phase`}
        value={`${row.deadlinePassed ? 'locked' : 'open'} · ${row.resultsRevealed ? 'results in' : 'awaiting results'}`}
      />
      <Row label="fixtures" value={`${row.finishedCount}/${row.fixtureCount} finished`} />
      <Row label="users predicted" value={String(row.predictedUsers)} />
    </>
  )
}
